"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchExpenses, fetchCategories, createExpense, updateExpense, deleteExpense } from "@/state/expenses/expenses.slice";
import { format } from "date-fns";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Money, Plus, MagnifyingGlass, FunnelSimple, PencilSimple, Trash } from "@phosphor-icons/react";
import { ChevronLeft, ChevronRight, Loader2, Calendar, DollarSign, TrendingUp } from "lucide-react";

// Category colors
const getCategoryColor = (category) => {
  const colors = {
    Rent: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400",
    Travel: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
    Salary: "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400",
    Utility: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400",
    Supplies: "bg-cyan-500",
    Marketing: "bg-pink-500",
    Maintenance: "bg-yellow-500",
    Other: "bg-gray-50 dark:bg-slate-8000",
  };
  return colors[category] || "bg-gray-400";
};

// Common expense categories
const EXPENSE_CATEGORIES = [
  "Rent",
  "Travel",
  "Salary",
  "Utility",
  "Supplies",
  "Marketing",
  "Maintenance",
  "Equipment",
  "Insurance",
  "Other",
];

// Form schema
const expenseSchema = z.object({
  amount: z.string().min(1, "Amount is required"),
  description: z.string().min(1, "Description is required"),
  category: z.string().min(1, "Category is required"),
  date: z.string().min(1, "Date is required"),
});

export default function ExpensesPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();

  const { expenses, categories, pagination, summary, isLoading } = useSelector((state) => state.expenses);

  // Filter state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  // Default to today's data
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  // Form
  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      amount: "",
      description: "",
      category: "",
      date: format(new Date(), "yyyy-MM-dd"),
    },
  });

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch expenses when filters change
  useEffect(() => {
    dispatch(fetchExpenses({
      page,
      limit,
      search: debouncedSearch,
      category: selectedCategory === "all" ? "" : selectedCategory,
      startDate,
      endDate,
    }));
  }, [dispatch, page, limit, debouncedSearch, selectedCategory, startDate, endDate]);

  // Fetch categories on mount
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isFormOpen && editingExpense) {
      reset({
        amount: editingExpense.amount?.toString() || "",
        description: editingExpense.description || "",
        category: editingExpense.category || "",
        date: editingExpense.date ? format(new Date(editingExpense.date), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
      });
    } else if (isFormOpen) {
      reset({
        amount: "",
        description: "",
        category: "",
        date: format(new Date(), "yyyy-MM-dd"),
      });
    }
  }, [isFormOpen, editingExpense, reset]);

  // Quick date range presets
  const setDateRange = useCallback((preset) => {
    const today = new Date();
    let start = new Date();

    switch (preset) {
      case "today":
        start = today;
        break;
      case "week":
        start.setDate(today.getDate() - 7);
        break;
      case "month":
        start.setMonth(today.getMonth() - 1);
        break;
      case "all":
        setStartDate("");
        setEndDate("");
        setPage(1);
        return;
      default:
        return;
    }

    setStartDate(format(start, "yyyy-MM-dd"));
    setEndDate(format(today, "yyyy-MM-dd"));
    setPage(1);
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearch("");
    setSelectedCategory("all");
    setStartDate("");
    setEndDate("");
    setPage(1);
  }, []);

  // Handlers
  const handleAddExpense = useCallback(() => {
    setEditingExpense(null);
    setIsFormOpen(true);
  }, []);

  const handleEditExpense = useCallback((expense) => {
    setEditingExpense(expense);
    setIsFormOpen(true);
  }, []);

  const handleDeleteClick = useCallback((expense) => {
    setExpenseToDelete(expense);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!expenseToDelete) return;

    try {
      await dispatch(deleteExpense(expenseToDelete.id)).unwrap();
      toast({ title: "Success", description: "Expense deleted successfully" });
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error });
    } finally {
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
    }
  }, [dispatch, expenseToDelete, toast]);

  const handleFormSubmit = useCallback(async (data) => {
    try {
      const expenseData = {
        ...data,
        amount: parseFloat(data.amount),
      };

      if (editingExpense) {
        await dispatch(updateExpense({ id: editingExpense.id, expenseData })).unwrap();
        toast({ title: "Success", description: "Expense updated successfully" });
      } else {
        await dispatch(createExpense(expenseData)).unwrap();
        toast({ title: "Success", description: "Expense added successfully" });
      }
      setIsFormOpen(false);
      setEditingExpense(null);
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error });
    }
  }, [dispatch, editingExpense, toast]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
    }).format(amount);
  };

  // All categories for dropdown (merge predefined and existing)
  const allCategories = useMemo(() => {
    const merged = new Set([...EXPENSE_CATEGORIES, ...categories]);
    return Array.from(merged);
  }, [categories]);

  return (
    <div className="mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Money className="h-8 w-8" />
            Expenses
          </h1>
          <p className="text-muted-foreground mt-1">
            Track and manage business expenses
          </p>
        </div>
        <Button onClick={handleAddExpense} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Expense
        </Button>
      </div>

      {/* Summary Cards - Compact Enterprise Style */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-900 border border-red-100 dark:border-red-800 shadow-sm">
          <div className="p-2 bg-red-50 dark:bg-red-900/30">
            <DollarSign className="h-5 w-5 text-red-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Total Expenses</p>
            <p className="text-lg font-bold text-red-600">{formatCurrency(summary?.totalAmount || 0)}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-900 border shadow-sm">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/30">
            <TrendingUp className="h-5 w-5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Expense Count</p>
            <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{pagination?.total || 0}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-900 border shadow-sm">
          <div className="p-2 bg-purple-50 dark:bg-purple-900/30">
            <Money className="h-5 w-5 text-purple-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Average Expense</p>
            <p className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {formatCurrency((summary?.totalAmount || 0) / Math.max(summary?.count || 1, 1))}
            </p>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FunnelSimple className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search expenses..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={selectedCategory} onValueChange={(val) => { setSelectedCategory(val); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {allCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                className="flex-1"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                className="flex-1"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setDateRange("today")}>Today</Button>
            <Button variant="outline" size="sm" onClick={() => setDateRange("week")}>Last 7 Days</Button>
            <Button variant="outline" size="sm" onClick={() => setDateRange("month")}>Last 30 Days</Button>
            <Button variant="outline" size="sm" onClick={() => setDateRange("all")}>All Time</Button>
            <Button variant="ghost" size="sm" onClick={clearFilters}>Clear Filters</Button>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : expenses.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Money className="h-12 w-12 mb-4" />
            <p className="text-lg">No expenses found</p>
            <p className="text-sm">Add your first expense to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="enterprise-table-container">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Recorded By</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(expense.date), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="font-medium max-w-xs truncate">
                    {expense.description}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${getCategoryColor(expense.category)} text-white`}>
                      {expense.category || "Uncategorized"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-semibold text-red-600">
                    {formatCurrency(expense.amount)}
                  </TableCell>
                  <TableCell>
                    {expense.user ? (
                      <span className="text-sm">
                        {expense.user.first_name || expense.user.username}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEditExpense(expense)}>
                        <PencilSimple className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(expense)} className="text-red-500 hover:text-red-600">
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Rows per page</span>
            <Select value={limit.toString()} onValueChange={(value) => { setLimit(parseInt(value)); setPage(1); }}>
              <SelectTrigger className="w-[70px]">
                <SelectValue placeholder={limit} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1 || isLoading}>
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-sm font-medium">
              Page {pagination.page} of {pagination.pages}
            </div>
            <Button variant="outline" size="sm" onClick={() => setPage(Math.min(pagination.pages, page + 1))} disabled={page >= pagination.pages || isLoading}>
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingExpense ? "Edit Expense" : "Add New Expense"}</DialogTitle>
            <DialogDescription>
              {editingExpense ? "Update expense details." : "Record a new business expense."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <Input {...field} type="number" step="0.01" placeholder="0.00" />
                )}
              />
              {errors.amount && <p className="text-red-500 text-sm">{errors.amount.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {allCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.category && <p className="text-red-500 text-sm">{errors.category.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Controller
                name="date"
                control={control}
                render={({ field }) => <Input {...field} type="date" />}
              />
              {errors.date && <p className="text-red-500 text-sm">{errors.date.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Textarea {...field} placeholder="Enter expense description..." rows={3} />
                )}
              />
              {errors.description && <p className="text-red-500 text-sm">{errors.description.message}</p>}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {editingExpense ? "Update" : "Add Expense"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
